import { RoleType } from '../types/api';
import { AdminSection } from '../types/admin';
import { hasPermission } from '../utils/security';

export type AppTab =
  | 'dashboard'
  | 'reports'
  | 'complaints'
  | 'workorders'
  | 'my-workorders'
  | 'officer'
  | 'supervisor'
  | 'vendors'
  | 'technician'
  | 'masters'
  | 'admin-config'
  | 'profile';

export interface NavDefinition {
  id: AppTab;
  label: string;
  roles: RoleType[];
}

export interface AdminSectionDefinition {
  id: AdminSection;
  label: string;
}

export interface AdminGroupDefinition {
  group: string;
  items: AdminSectionDefinition[];
}

export const ALL_ROLES: RoleType[] = [
  'ADMIN', 'OFFICER', 'SUPERVISOR', 'DEPARTMENT_USER', 'VENDOR', 'TECHNICIAN',
];

export const PRIMARY_NAV_MODEL: NavDefinition[] = [
  { id: 'dashboard', label: 'Dashboard', roles: ALL_ROLES },
  { id: 'reports', label: 'Reports', roles: ['ADMIN', 'OFFICER', 'SUPERVISOR'] },
  { id: 'complaints', label: 'Complaints', roles: ['ADMIN', 'OFFICER', 'SUPERVISOR', 'DEPARTMENT_USER'] },
  { id: 'workorders', label: 'Work Orders', roles: ['ADMIN', 'OFFICER'] },
  { id: 'my-workorders', label: 'My Work Orders', roles: ['VENDOR'] },
  { id: 'officer', label: 'Officer Triage', roles: ['ADMIN', 'OFFICER'] },
  { id: 'supervisor', label: 'Supervisor', roles: ['ADMIN', 'SUPERVISOR'] },
  { id: 'vendors', label: 'Vendors', roles: ['ADMIN'] },
  { id: 'technician', label: 'Technicians', roles: ['ADMIN', 'TECHNICIAN'] },
  { id: 'masters', label: 'Masters', roles: ['ADMIN'] },
  { id: 'admin-config', label: 'Admin Config', roles: ['ADMIN'] },
];

export const ADMIN_GROUPS_MODEL: AdminGroupDefinition[] = [
  {
    group: 'Organization',
    items: [{ id: 'organization', label: 'Plants & Locations' }],
  },
  {
    group: 'Classification',
    items: [
      { id: 'departments', label: 'Departments' },
      { id: 'categories', label: 'Categories' },
      { id: 'subcategories', label: 'Subcategories' },
      { id: 'priorities', label: 'Priorities' },
      { id: 'statuses', label: 'Statuses' },
    ],
  },
  {
    group: 'Workflow',
    items: [
      { id: 'sla-rules', label: 'SLA Rules' },
      { id: 'notification-rules', label: 'Notification Rules' },
      { id: 'custom-fields', label: 'Custom Fields' },
    ],
  },
  {
    group: 'System',
    items: [
      { id: 'number-patterns', label: 'Number Patterns' },
      { id: 'users', label: 'Users & Roles' },
      { id: 'roles-permissions', label: 'Roles & Permissions' },
      { id: 'general-settings', label: 'General Settings' },
    ],
  },
  {
    group: 'Security',
    items: [
      { id: 'audit-logs', label: 'Audit Logs' },
      { id: 'security-settings', label: 'Security Settings' },
      { id: 'login-history', label: 'Login History' },
    ],
  },
];

export const LANDING_BY_ROLE: Record<RoleType, AppTab> = {
  ADMIN: 'dashboard',
  OFFICER: 'dashboard',
  SUPERVISOR: 'dashboard',
  DEPARTMENT_USER: 'complaints',
  VENDOR: 'dashboard',
  TECHNICIAN: 'dashboard',
};

export const canSeeNav = (item: NavDefinition, role: RoleType | undefined): boolean =>
  !!role && item.roles.includes(role);

export const canSeeAdminConfig = (role: RoleType | undefined): boolean =>
  hasPermission(role, 'config:update');

export const canRaiseComplaint = (role: RoleType | undefined): boolean =>
  hasPermission(role, 'complaint:create');
